import { useQuery } from '@tanstack/react-query';
import { motion } from 'motion/react';
import type { Token } from '@/shared/assets/tokens';
import { SkeletonLine } from '@/shared/components/atoms/LoadingSkeleton';
import { CustomNumberFlow } from '@/shared/components/CustomNumberFlow';
import { TokenLogo } from '@/shared/components/logo/TokenLogo';
import { GridFrame } from '@/shared/components/swap-flow/BraggingCard/GridFrame';
import usePriceDelta from '@/shared/hooks/usePriceDelta';
import useSupportedAssets from '@/shared/hooks/useSupportedAssets';
import ArrowIcon from '@/shared/icons/flip-ui-kit/large/Arrow';
import CheckCircleIcon from '@/shared/icons/flip-ui-kit/small/CheckCircle';
import { integrationManager } from '@/shared/integrations';
import { chainflipAssetMap } from '@/shared/utils/env';
import TokenAmount from '@/shared/utils/TokenAmount';

const TokenCard = ({ token }: { token: Token }) => (
  <div className="rounded-md border border-[#2D2D2D] p-2 [background:linear-gradient(180deg,#252525_0%,#1D1D1D_100%)]">
    <TokenLogo token={token} size="large" />
  </div>
);

export const BraggingCard = () => {
  const srcToken = chainflipAssetMap.Btc;
  const destToken = chainflipAssetMap.Usdc;
  const srcAmount = TokenAmount.fromWholeUnits(1, srcToken.decimals);
  const supportedAssets = useSupportedAssets();

  const { data: quote } = useQuery({
    queryKey: ['bragging-quote'],
    queryFn: async () => {
      const chainflipIntegration = integrationManager.getIntegration('chainflip');
      const routes = await chainflipIntegration.getRoutes({
        srcChainId: srcToken.chain.id,
        srcTokenAddress: srcToken.address,
        destChainId: destToken.chain.id,
        destTokenAddress: destToken.address,
        amount: srcAmount.toBigInt(),
      });

      return routes.toSorted((r1, r2) => r2.destAmount.toNumber() - r1.destAmount.toNumber()).at(0);
    },
    refetchInterval: 30000,
  });

  const { delta, color: deltaColor } = usePriceDelta({
    srcAmount: quote?.srcAmount,
    destAmount: quote?.destAmount,
    srcToken: quote?.srcToken,
    destToken: quote?.destToken,
  });

  const isBnbSupported = supportedAssets.includes('Bnb');
  const isCbbtcSupported = supportedAssets.includes('Cbbtc');

  const tokens = [
    chainflipAssetMap.Btc,
    chainflipAssetMap.Eth,
    chainflipAssetMap.Sol,
    chainflipAssetMap.Flip,
    chainflipAssetMap.HubDot,
    chainflipAssetMap.Usdc,
    chainflipAssetMap.Usdt,
    chainflipAssetMap.Trx,
    // TODO(2.3): Simply include Bnb & Cbbtc here after 2.3 goes live on all networks
    isBnbSupported && chainflipAssetMap.Bnb,
    isCbbtcSupported && chainflipAssetMap.Cbbtc,
  ].filter(Boolean) as Token[];

  return (
    <div className="relative flex w-full flex-col gap-6 overflow-hidden rounded-md border border-cf-gray-4 bg-cf-gray-3 p-4 shadow-grayPop1 md:w-[430px]">
      <GridFrame className="pointer-events-none absolute left-0 top-0" />
      <motion.div
        className="flex flex-row gap-8 py-4"
        animate={{
          x: [-tokens.length * 82, 0], // 50px token card width + 32px gap
          transition: {
            ease: 'linear',
            duration: 20,
            repeat: Infinity,
          },
        }}
      >
        {tokens.map((token) => (
          <TokenCard key={token.chainflipId} token={token} />
        ))}
        {/* render tokens two times for infinite slider */}
        {tokens.map((token) => (
          <TokenCard key={token.chainflipId} token={token} />
        ))}
      </motion.div>
      <div className="absolute inset-x-0 top-0 h-[115px] [background:linear-gradient(90deg,#161616_0%,rgba(22,22,22,0.00)_50%,#161616_100%)]" />
      <div className="flex flex-col items-center font-aeonikMedium text-cf-light-2">
        <div className="text-20 leading-6">
          <span className="text-cf-white">Excellent</span> pricing.
        </div>
        <div className="text-20 leading-6">
          Emphasis on <span className="text-cf-white">excellent</span>.
        </div>
        <div className="flex flex-row items-center gap-1 pt-4">
          <span className="text-30 text-cf-white">{srcAmount.toCapped()}</span>
          <span className="text-14">{srcToken.symbol}</span>
          <ArrowIcon direction="right" />
          {quote ? (
            <span className="-mt-1.5 text-30 text-cf-white">
              <CustomNumberFlow
                value={quote.destAmount.toCapped().replaceAll(',', '')}
                format={
                  quote.destAmount.gt(1000 * 10 ** destToken.decimals)
                    ? { maximumFractionDigits: 0 }
                    : { minimumFractionDigits: 2 }
                }
              />
            </span>
          ) : (
            <SkeletonLine width={40} />
          )}
          <span className="text-14">{destToken.symbol}</span>
        </div>
        <div className="text-12">
          {delta ? (
            <span className={deltaColor}>({delta.toFixed(1)}%)</span>
          ) : (
            <SkeletonLine width={30} inline />
          )}{' '}
          Est. price delta
        </div>
      </div>
      <div className="flex flex-row justify-between gap-2 px-2 pt-8 font-aeonikMedium text-12 text-cf-light-2">
        <div className="flex flex-row items-center gap-x-1">
          <CheckCircleIcon />
          <div>Native assets</div>
        </div>
        <div className="flex flex-row items-center gap-x-1">
          <CheckCircleIcon />
          <div>Better pricing</div>
        </div>
        <div className="flex flex-row items-center gap-x-1">
          <CheckCircleIcon />
          <div>No wallet needed</div>
        </div>
      </div>
    </div>
  );
};
